import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "wouter";
import { io, Socket } from "socket.io-client";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getApiUrl, API_URL, ApiError } from "@/lib/api";
import { STADIUM_PRESETS } from "@/data/stadiumPresets";
import {
  isTerminalTrackingStatus,
  type TrackingSessionStatus,
} from "@shared/tracking";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PublicSession {
  id: string;
  busNumber: string;
  eventName: string;
  status: TrackingSessionStatus;
  driverLastSeen: string | null;
  boardedCount: number | null;
  totalPassengers: number | null;
  passengerOpenCount: number | null;
}

type PageState = "loading" | "error" | "ready" | "tracking";
type Mode = "boarding" | "transit";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isOnline(lastSeen: string | null): boolean {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < 2 * 60 * 1000;
}

// ─── Status message presets ───────────────────────────────────────────────────

const STATUS_CHIPS = [
  "Meet here — look for red flag",
  "Bus loading now",
  "Bus FULL — go to next bus",
  "Departing in 5 min",
  "We are at Gate D",
];

// ─── Pulsing meetup marker CSS (injected once) ────────────────────────────────

const MEETUP_CSS = `
.meetup-pulse {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f97316;
  box-shadow: 0 0 0 0 rgba(249,115,22,0.7);
  animation: meetup-ring 1.4s infinite;
}
@keyframes meetup-ring {
  0%   { box-shadow: 0 0 0 0 rgba(249,115,22,0.7); }
  70%  { box-shadow: 0 0 0 14px rgba(249,115,22,0); }
  100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
}
`;

function injectMeetupCss() {
  if (document.getElementById("meetup-pulse-style")) return;
  const style = document.createElement("style");
  style.id = "meetup-pulse-style";
  style.textContent = MEETUP_CSS;
  document.head.appendChild(style);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TrackCoordinatorPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  // Page lifecycle
  const [state, setState] = useState<PageState>("loading");
  const [session, setSession] = useState<PublicSession | null>(null);
  const [errorMsg, setErrorMsg] = useState(
    "This tracking link has expired or is invalid.",
  );

  // Coordinator UI state
  const [mode, setMode] = useState<Mode>("boarding");
  const [boardedCount, setBoardedCount] = useState(0);
  const [passengerOpenCount, setPassengerOpenCount] = useState(0);
  const [tokenError, setTokenError] = useState(false);
  const [wakeLockWarn, setWakeLockWarn] = useState(false);
  const [gpsError, setGpsError] = useState(false);
  const [driverOnline, setDriverOnline] = useState(false);
  const [meetupDropped, setMeetupDropped] = useState(false);
  const [meetupLabel, setMeetupLabel] = useState("Meetup point");
  const [allAboardModal, setAllAboardModal] = useState(false);
  const [myPos, setMyPos] = useState<{ lat: number; lng: number } | null>(null);
  const [sentChip, setSentChip] = useState<string | null>(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(0);
  const [selectedGateLabel, setSelectedGateLabel] = useState<string | null>(
    null,
  );

  // Refs
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const myMarkerRef = useRef<maplibregl.Marker | null>(null);
  const driverMarkerRef = useRef<maplibregl.Marker | null>(null);
  const meetupMarkerRef = useRef<maplibregl.Marker | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const myPosRef = useRef<{ lat: number; lng: number } | null>(null);
  const meetupLabelRef = useRef("Meetup point");
  const sessionInitialized = useRef(false);
  const chipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep meetupLabelRef in sync
  useEffect(() => {
    meetupLabelRef.current = meetupLabel;
  }, [meetupLabel]);

  // Keep myPosRef in sync with state
  useEffect(() => {
    myPosRef.current = myPos;
  }, [myPos]);

  // ── Load session ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!sessionId || !UUID_RE.test(sessionId)) {
      setErrorMsg("This tracking link has expired or is invalid.");
      setState("error");
      return;
    }

    let cancelled = false;

    fetch(getApiUrl(`/api/tracking/sessions/${sessionId}/public`))
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new ApiError(
            res.status,
            (body as { message?: string }).message || "Not found",
          );
        }
        return res.json() as Promise<PublicSession>;
      })
      .then((data) => {
        if (cancelled) return;
        // Cancelled sessions are treated exactly like ended — tracking is over
        if (isTerminalTrackingStatus(data.status)) {
          setErrorMsg(
            "This event has ended. No further tracking is available.",
          );
          setState("error");
          return;
        }
        setSession(data);
        setBoardedCount(data.boardedCount ?? 0);
        setPassengerOpenCount(data.passengerOpenCount ?? 0);
        setDriverOnline(isOnline(data.driverLastSeen));
        setState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Default error message is correct
        } else {
          setErrorMsg(
            "Unable to load tracking info. Please check your connection.",
          );
        }
        setState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // ── Wake Lock reacquire on tab visibility ─────────────────────────────────

  const reacquireWakeLock = useCallback(async () => {
    if (
      wakeLockRef.current !== null &&
      wakeLockRef.current.released &&
      "wakeLock" in navigator &&
      document.visibilityState === "visible"
    ) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // Best-effort
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", reacquireWakeLock);
    return () =>
      document.removeEventListener("visibilitychange", reacquireWakeLock);
  }, [reacquireWakeLock]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      socketRef.current?.disconnect();
      wakeLockRef.current?.release().catch(() => {});
      mapRef.current?.remove();
      mapRef.current = null;
      sessionInitialized.current = false; // allow re-init if component is reused
      if (chipTimerRef.current !== null) {
        clearTimeout(chipTimerRef.current);
      }
    };
  }, []);

  // ── Initialize MapLibre after entering tracking state ─────────────────────

  useEffect(() => {
    if (state !== "tracking") return;
    if (!mapContainerRef.current) return;
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    injectMeetupCss();

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [-94.5786, 39.0997], // Kansas City default
      zoom: 12,
    });
    mapRef.current = map;

    // Coordinator marker (yellow flag)
    const coordEl = document.createElement("div");
    coordEl.innerHTML = `
      <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 9.75 14 22 14 22s14-12.25 14-22C28 6.27 21.73 0 14 0z" fill="#FBBF24"/>
        <text x="14" y="19" text-anchor="middle" font-size="12" font-family="system-ui,sans-serif" fill="#1f2937" font-weight="700">ME</text>
      </svg>`;
    coordEl.style.cursor = "default";

    const coordMarker = new maplibregl.Marker({ element: coordEl });
    myMarkerRef.current = coordMarker;

    // Driver marker (blue bus)
    const driverEl = document.createElement("div");
    driverEl.innerHTML = `
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="#3B82F6" stroke="#1D4ED8" stroke-width="2"/>
        <text x="16" y="21" text-anchor="middle" font-size="14" font-family="system-ui,sans-serif">🚌</text>
      </svg>`;

    const driverMarker = new maplibregl.Marker({ element: driverEl });
    driverMarkerRef.current = driverMarker;

    // Center on current position if available
    if (myPosRef.current) {
      map.setCenter([myPosRef.current.lng, myPosRef.current.lat]);
      coordMarker
        .setLngLat([myPosRef.current.lng, myPosRef.current.lat])
        .addTo(map);
    }
  }, [state]);

  // Passenger count is now driven by socket heartbeat — no fallback polling needed

  // ── API helpers (token-based, no JWT) ────────────────────────────────────

  const patchBoarding = useCallback(
    async (count: number) => {
      if (!sessionId) return;
      try {
        await fetch(
          getApiUrl(
            `/api/tracking/sessions/${sessionId}/boarding?token=${encodeURIComponent(token)}`,
          ),
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ boardedCount: count }),
          },
        );
      } catch {
        // Non-critical — Socket.IO already notified participants
      }
    },
    [sessionId, token],
  );

  const patchMeetup = useCallback(
    async (lat: number, lng: number, label: string) => {
      if (!sessionId) return;
      try {
        await fetch(
          getApiUrl(
            `/api/tracking/sessions/${sessionId}/meetup?token=${encodeURIComponent(token)}`,
          ),
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lat, lng, label }),
          },
        );
      } catch {
        // Non-critical
      }
    },
    [sessionId, token],
  );

  const patchStatusMessage = useCallback(
    async (message: string) => {
      if (!sessionId) return;
      try {
        await fetch(
          getApiUrl(
            `/api/tracking/sessions/${sessionId}/status-message?token=${encodeURIComponent(token)}`,
          ),
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message }),
          },
        );
      } catch {
        // Non-critical
      }
    },
    [sessionId, token],
  );

  // ── Start tracking ────────────────────────────────────────────────────────

  const startTracking = useCallback(async () => {
    if (!session || !sessionId) return;

    // 1. Wake Lock
    if ("wakeLock" in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        setWakeLockWarn(true);
      }
    } else {
      setWakeLockWarn(true);
    }

    // 2. Socket.IO — namespace must be in the URL, not in options
    // Disconnect any previous socket before creating a new one (guards against double-mount)
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    const base = API_URL || window.location.origin;
    const socket = io(`${base}/tracking`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    // Join inside the connect handler so the session is re-joined after every
    // reconnect — server-side join state dies with the old connection, and
    // un-joined location emits are silently dropped
    socket.on("connect", () => {
      socket.emit("join-tracking-session", {
        sessionId,
        role: "coordinator",
        token,
      });
    });

    socket.on("join-error", ({ message }: { message: string }) => {
      console.warn("join-error:", message);
      setTokenError(true);
    });

    socket.on(
      "driver-location",
      ({ lat, lng }: { lat: number; lng: number }) => {
        setDriverOnline(true);
        const marker = driverMarkerRef.current;
        const map = mapRef.current;
        if (!marker || !map) return;
        if (!map.hasImage) {
          // map not fully ready yet — defer
          return;
        }
        marker.setLngLat([lng, lat]);
        if (!marker.getLngLat) {
          marker.addTo(map);
        } else {
          // marker already positioned — just ensure it's on the map
          try {
            marker.addTo(map);
          } catch {
            // already added
          }
        }
      },
    );

    socket.on(
      "boarding-update",
      ({ boardedCount: count }: { boardedCount: number }) => {
        setBoardedCount(count);
      },
    );

    socket.on(
      "passenger-count-update",
      ({ passengerOpenCount: count }: { passengerOpenCount: number }) => {
        setPassengerOpenCount(count);
      },
    );

    // 3. GPS watchPosition
    if (!navigator.geolocation) {
      setGpsError(true);
      setState("tracking");
      return;
    }

    setState("tracking");

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setMyPos({ lat, lng });

        // Update own marker on map
        const marker = myMarkerRef.current;
        const map = mapRef.current;
        if (marker && map) {
          marker.setLngLat([lng, lat]);
          try {
            marker.addTo(map);
          } catch {
            // already added
          }
          // Pan to position only once
          if (!myPosRef.current) {
            map.flyTo({ center: [lng, lat], zoom: 15 });
          }
        }

        socket.emit("coordinator-location", {
          lat,
          lng,
          accuracy,
          timestamp: Date.now(),
        });
      },
      (err) => {
        if (
          err.code === err.PERMISSION_DENIED ||
          err.code === err.POSITION_UNAVAILABLE ||
          err.code === err.TIMEOUT
        ) {
          setGpsError(true);
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
    );
  }, [session, sessionId, token]);

  // ── Boarding counter handlers ──────────────────────────────────────────────

  const handleIncrement = useCallback(() => {
    // Functional updater avoids stale closure on rapid taps
    setBoardedCount((prev) => {
      const next = prev + 1;
      socketRef.current?.emit("boarding-update", { boardedCount: next });
      void patchBoarding(next);
      return next;
    });
  }, [patchBoarding]);

  const handleDecrement = useCallback(() => {
    setBoardedCount((prev) => {
      const next = Math.max(0, prev - 1);
      socketRef.current?.emit("boarding-update", { boardedCount: next });
      void patchBoarding(next);
      return next;
    });
  }, [patchBoarding]);

  // ── Meetup pin ────────────────────────────────────────────────────────────

  const handleDropMeetup = useCallback(() => {
    const pos = myPosRef.current;
    if (!pos) return;

    const { lat, lng } = pos;
    const label = meetupLabelRef.current;

    // Create or update pulsing orange marker
    const map = mapRef.current;
    if (map) {
      if (meetupMarkerRef.current) {
        meetupMarkerRef.current.setLngLat([lng, lat]);
      } else {
        const el = document.createElement("div");
        el.className = "meetup-pulse";
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(map);
        meetupMarkerRef.current = marker;
      }
      map.flyTo({ center: [lng, lat], zoom: 15 });
    }

    setMeetupDropped(true);
    socketRef.current?.emit("meetup-update", { lat, lng, label });
    void patchMeetup(lat, lng, label);
  }, [patchMeetup]);

  const handleMeetupLabelChange = useCallback(
    (newLabel: string) => {
      setMeetupLabel(newLabel);
      meetupLabelRef.current = newLabel;
      const pos = myPosRef.current;
      if (!pos || !meetupDropped) return;
      socketRef.current?.emit("meetup-update", {
        lat: pos.lat,
        lng: pos.lng,
        label: newLabel,
      });
    },
    [meetupDropped],
  );

  // ── Gate preset handler ───────────────────────────────────────────────────

  const handleGatePreset = useCallback(
    (lat: number, lng: number, label: string) => {
      setSelectedGateLabel(label);
      setMeetupLabel(label);
      meetupLabelRef.current = label;

      const map = mapRef.current;
      if (map) {
        if (meetupMarkerRef.current) {
          meetupMarkerRef.current.setLngLat([lng, lat]);
        } else {
          const el = document.createElement("div");
          el.className = "meetup-pulse";
          const marker = new maplibregl.Marker({ element: el })
            .setLngLat([lng, lat])
            .addTo(map);
          meetupMarkerRef.current = marker;
        }
        map.flyTo({ center: [lng, lat], zoom: 16 });
      }

      setMeetupDropped(true);
      socketRef.current?.emit("meetup-update", { lat, lng, label });
      void patchMeetup(lat, lng, label);
    },
    [patchMeetup],
  );

  // ── Status chip handler ───────────────────────────────────────────────────

  const handleChip = useCallback(
    (message: string) => {
      setSentChip(message);
      socketRef.current?.emit("status-broadcast", { message });
      void patchStatusMessage(message);
      // Reset chip highlight after 3 seconds — use ref to clear on unmount
      if (chipTimerRef.current !== null) clearTimeout(chipTimerRef.current);
      chipTimerRef.current = setTimeout(() => setSentChip(null), 3000);
    },
    [patchStatusMessage],
  );

  // ── All Aboard ────────────────────────────────────────────────────────────

  const handleAllAboardConfirm = useCallback(() => {
    socketRef.current?.emit("all-aboard");
    setAllAboardModal(false);
  }, []);

  // ─── Render: loading ──────────────────────────────────────────────────────

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div
          role="status"
          aria-label="Loading"
          className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"
        />
      </div>
    );
  }

  // ─── Render: error ────────────────────────────────────────────────────────

  if (state === "error") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🚫</div>
          <h1 className="text-xl font-bold text-white mb-2">
            Link Unavailable
          </h1>
          <p className="text-gray-400 text-sm">{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ─── Render: ready (pre-start screen) ────────────────────────────────────

  if (state === "ready") {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
          <div className="text-center">
            <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-1">
              Coordinator
            </p>
            <h1 className="text-4xl font-black text-white">
              Bus {session!.busNumber}
            </h1>
            <p className="text-gray-400 mt-2 text-lg">{session!.eventName}</p>
          </div>

          <div className="bg-blue-400/10 border border-blue-400/30 rounded-xl px-5 py-4 max-w-sm w-full">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="text-blue-400 text-xl">
                📍
              </span>
              <p className="text-blue-200 text-sm leading-relaxed">
                Share your location, manage boarding, and drop a{" "}
                <strong>meetup pin</strong> so passengers can find you.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-400 active:scale-95 text-white font-black text-xl py-5 px-12 rounded-2xl shadow-lg shadow-blue-500/20 transition-all w-full max-w-sm"
            onClick={() => void startTracking()}
          >
            Start Tracking
          </button>
        </div>

        <p className="text-center text-gray-600 text-xs pb-6">
          RutaLink Fleet Tracker
        </p>
      </div>
    );
  }

  // ─── Render: tracking ─────────────────────────────────────────────────────

  const total = session?.totalPassengers ?? null;
  const totalDisplay = total !== null ? `/${total}` : "";
  const mapHeightClass =
    mode === "transit" ? "flex-1" : "h-[40vh] flex-shrink-0";

  // Passenger tracking count color logic
  const passengerDotColor =
    passengerOpenCount === 0
      ? "bg-gray-500"
      : total !== null && passengerOpenCount >= total * 0.8
        ? "bg-green-500"
        : total !== null && passengerOpenCount < total * 0.5
          ? "bg-amber-400"
          : "bg-green-500";
  const passengerTextColor =
    passengerOpenCount === 0
      ? "text-gray-400"
      : total !== null && passengerOpenCount >= total * 0.8
        ? "text-green-400"
        : total !== null && passengerOpenCount < total * 0.5
          ? "text-amber-400"
          : "text-green-400";

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* All-aboard confirmation modal */}
      {allAboardModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
            <div className="text-5xl mb-3">🚌</div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">
              All Aboard?
            </h2>
            <p className="text-gray-600 mb-6 text-sm">
              This will notify the driver and all passengers that everyone is on
              board.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 rounded-xl"
                onClick={() => setAllAboardModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 bg-green-500 hover:bg-green-400 text-white font-bold py-3 rounded-xl"
                onClick={handleAllAboardConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token error banner */}
      {tokenError && (
        <div className="bg-red-700 px-4 py-3 flex-shrink-0">
          <p className="text-white text-sm font-semibold">
            Invalid coordinator token — contact ops
          </p>
        </div>
      )}

      {/* Wake lock warning */}
      {wakeLockWarn && (
        <div className="bg-yellow-600/80 px-4 py-2 flex-shrink-0">
          <p className="text-yellow-100 text-xs">
            Screen may turn off. Keep your phone awake manually.
          </p>
        </div>
      )}

      {/* GPS error */}
      {gpsError && (
        <div className="bg-orange-700 px-4 py-2 flex-shrink-0">
          <p className="text-white text-xs">
            Location access denied. Enable GPS in browser settings then reload.
          </p>
        </div>
      )}

      {/* Status Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center gap-3 flex-shrink-0 flex-wrap min-h-[44px]">
        <span className="text-white font-semibold text-sm">
          Bus {session?.busNumber}
        </span>
        <span className="text-gray-400 text-xs truncate">
          {session?.eventName}
        </span>
        <div className="flex items-center gap-1.5 ml-auto">
          <span
            className={`w-2 h-2 rounded-full ${driverOnline ? "bg-green-500" : "bg-gray-500"}`}
            aria-hidden="true"
          />
          <span className="text-xs text-gray-300">
            Driver: {driverOnline ? "online" : "offline"}
          </span>
        </div>

        {/* Passenger tracking count */}
        <div className="flex items-center gap-1.5">
          <span
            className={`w-2 h-2 rounded-full ${passengerDotColor}`}
            aria-hidden="true"
          />
          <span className={`text-xs font-semibold ${passengerTextColor}`}>
            {passengerOpenCount}
            {total !== null ? `/${total}` : ""} tracking
          </span>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-gray-700 rounded-lg p-0.5 ml-2">
          <button
            type="button"
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
              mode === "boarding"
                ? "bg-blue-500 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            onClick={() => {
              setMode("boarding");
              socketRef.current?.emit("status-broadcast", {
                message: "Mode: Boarding",
              });
            }}
          >
            Boarding
          </button>
          <button
            type="button"
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
              mode === "transit"
                ? "bg-blue-500 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            onClick={() => {
              setMode("transit");
              socketRef.current?.emit("status-broadcast", {
                message: "Mode: In Transit",
              });
            }}
          >
            Transit
          </button>
        </div>
      </div>

      {/* Map area */}
      <div
        className={`${mapHeightClass} relative border-b border-gray-700`}
        role="region"
        aria-label="Map"
      >
        <div ref={mapContainerRef} className="absolute inset-0" />

        {/* Meetup pin button — overlaid on map */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
          <button
            type="button"
            disabled={!myPos}
            className={`px-3 py-2 rounded-lg text-xs font-bold shadow-md transition-all ${
              myPos
                ? "bg-orange-500 hover:bg-orange-400 text-white"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"
            }`}
            onClick={handleDropMeetup}
          >
            {meetupDropped ? "Move meetup pin here" : "Drop meetup pin here"}
          </button>
        </div>

        {/* GPS indicator */}
        {myPos && (
          <div className="absolute bottom-3 right-3 z-10 bg-gray-900/80 rounded-lg px-2 py-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-400 text-xs font-semibold">
                GPS active
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Controls panel */}
      <div className="flex-1 min-h-0 bg-gray-900 overflow-y-auto">
        <div className="p-4 flex flex-col gap-4 pb-6">
          {/* Boarding counter */}
          <div
            className={`bg-gray-800 rounded-2xl p-4 ${mode === "boarding" ? "" : "opacity-70"}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wide">
                Boarding
              </h2>
              <span className="text-gray-400 text-xs">
                {myPos
                  ? `${myPos.lat.toFixed(4)}, ${myPos.lng.toFixed(4)}`
                  : "Awaiting GPS…"}
              </span>
            </div>

            {/* Large counter display */}
            <div className="text-center mb-4">
              <span className="text-6xl font-black text-white tabular-nums">
                {boardedCount}
              </span>
              <span className="text-3xl font-bold text-gray-500 tabular-nums">
                {totalDisplay}
              </span>
              <p className="text-gray-400 text-xs mt-1 uppercase tracking-widest">
                aboard
              </p>
            </div>

            {/* +1 / -1 buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                className="flex-1 bg-gray-700 hover:bg-gray-600 active:scale-95 text-white font-black text-2xl py-4 rounded-xl transition-all"
                onClick={handleDecrement}
                disabled={boardedCount <= 0}
                aria-label="Remove one passenger"
              >
                −
              </button>
              <button
                type="button"
                className="flex-2 flex-grow-[2] bg-green-500 hover:bg-green-400 active:scale-95 text-white font-black text-2xl py-4 rounded-xl transition-all shadow-md shadow-green-500/20"
                onClick={handleIncrement}
                aria-label="Add one passenger"
              >
                +1
              </button>
            </div>
          </div>

          {/* Meetup label (shown after pin drop) */}
          {meetupDropped && (
            <div className="bg-orange-900/40 border border-orange-500/30 rounded-xl p-3">
              <label className="block text-orange-300 text-xs font-semibold uppercase tracking-wide mb-2">
                Meetup pin label
              </label>
              <input
                type="text"
                value={meetupLabel}
                onChange={(e) => handleMeetupLabelChange(e.target.value)}
                className="w-full bg-gray-800 text-white text-sm rounded-lg px-3 py-2 border border-gray-600 focus:border-orange-400 focus:outline-none"
                placeholder="Meetup point"
              />
              <p className="text-orange-400/70 text-xs mt-1.5">
                Passengers see this label on their map.
              </p>
            </div>
          )}

          {/* Meetup Presets */}
          <div className="bg-gray-800 rounded-2xl overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              onClick={() => setPresetsOpen((o) => !o)}
              aria-expanded={presetsOpen}
            >
              <span className="text-white font-semibold text-sm uppercase tracking-wide">
                Meetup Presets
              </span>
              <span className="text-gray-400 text-lg leading-none">
                {presetsOpen ? "−" : "+"}
              </span>
            </button>

            {presetsOpen && (
              <div className="border-t border-gray-700 px-4 pb-4 pt-3">
                {/* Stadium tabs */}
                <div className="flex gap-2 mb-3">
                  {STADIUM_PRESETS.map((stadium, idx) => (
                    <button
                      key={stadium.name}
                      type="button"
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        selectedStadium === idx
                          ? "bg-orange-500 text-white"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      }`}
                      onClick={() => setSelectedStadium(idx)}
                    >
                      {stadium.name}
                    </button>
                  ))}
                </div>

                {/* Gate chips */}
                <div className="flex flex-wrap gap-2">
                  {STADIUM_PRESETS[selectedStadium]?.gates.map((gate) => (
                    <button
                      key={gate.label}
                      type="button"
                      className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all active:scale-95 ${
                        selectedGateLabel === gate.label
                          ? "bg-orange-500 border-orange-400 text-white"
                          : "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600"
                      }`}
                      onClick={() =>
                        handleGatePreset(gate.lat, gate.lng, gate.label)
                      }
                    >
                      {gate.label}
                    </button>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Tap a gate to drop the meetup pin. You can fine-tune the pin
                  afterwards.
                </p>
              </div>
            )}
          </div>

          {/* Status message chips */}
          <div className="bg-gray-800 rounded-2xl p-4">
            <h2 className="text-white font-semibold text-sm uppercase tracking-wide mb-3">
              Status Message
            </h2>
            <div className="flex flex-wrap gap-2">
              {STATUS_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all active:scale-95 ${
                    sentChip === chip
                      ? "bg-blue-500 border-blue-400 text-white"
                      : "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                  }`}
                  onClick={() => handleChip(chip)}
                >
                  {chip}
                </button>
              ))}
            </div>
            {sentChip && (
              <p className="text-blue-400 text-xs mt-2 font-semibold">
                Sent: {sentChip}
              </p>
            )}
          </div>

          {/* All Aboard button */}
          <button
            type="button"
            disabled={boardedCount === 0}
            className={`w-full py-5 rounded-2xl font-black text-xl transition-all active:scale-95 ${
              boardedCount > 0
                ? "bg-green-500 hover:bg-green-400 text-white shadow-lg shadow-green-500/20"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"
            }`}
            onClick={() => setAllAboardModal(true)}
          >
            ALL ABOARD
          </button>
        </div>
      </div>
    </div>
  );
}
